import { Box, Group, UnstyledButton } from "@mantine/core";
import React from "react";
import { RemovableChip } from "../../../components/common/RemovableChip";
import { ExpandedCriteriaChip } from "./ExpandedCriteriaChip";

export interface ReportCriteriaChip {
  id: number;
  label: string;
  /**
   * Set when this chip represents a group (Canonical / Position Profile). Each child is one
   * concrete Technology ID + display name — the user can remove individual children, and the
   * filter uses `children.map(c => c.id)` instead of the group chip's own `id`.
   * When all children are removed the group chip itself is removed.
   */
  children?: { id: number; label: string }[];
}

export interface ReportCriteriaBuilderProps {
  chips: ReportCriteriaChip[];
  onRemove: (id: number) => void;
  /** Called when the user removes one specific child from a group chip. */
  onRemoveFromGroup?: (chipId: number, childId: number) => void;
  onClearAll?: () => void;
  children?: React.ReactNode;
}

/**
 * Generic "pick a source → preview → add as removable chips" builder (FS_05_Reporting_01).
 * Renders whatever picker UI is passed as `children`, then the flat chip list below it.
 * Preview-before-commit (showing what a canonical/profile/family resolves to before adding it)
 * is each picker's responsibility — this component only owns the resulting flat list.
 */
export const ReportCriteriaBuilder: React.FC<ReportCriteriaBuilderProps> = ({
  chips,
  onRemove,
  onRemoveFromGroup,
  onClearAll,
  children,
}) => {
  return (
    <Box>
      {children && <Box mb="sm">{children}</Box>}
      {chips.length > 0 && (
        <Group gap="xs" wrap="wrap" mt="xs" align="flex-start" role="list" aria-label="Selected filter items">
          {chips.map((chip) =>
            chip.children ? (
              <ExpandedCriteriaChip
                key={chip.id}
                label={chip.label}
                items={chip.children}
                onRemoveItem={(childId) => onRemoveFromGroup?.(chip.id, childId)}
                onRemoveAll={() => onRemove(chip.id)}
              />
            ) : (
              <RemovableChip key={chip.id} label={chip.label} onRemove={() => onRemove(chip.id)} />
            )
          )}
          {onClearAll && (
            <UnstyledButton onClick={onClearAll} c="dimmed" fz="sm" td="underline">
              Clear all
            </UnstyledButton>
          )}
        </Group>
      )}
    </Box>
  );
};
